import { useMemo, useState, useCallback } from 'react';
import useAuthStore from '@entities/auth/model/useAuthStore';

import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import Button from '@shared/components/atoms/Button/Button';
import { getDefaultDateRange } from '@shared/utils/formatDate';
import VacationAdminDetailModal from '@pages/admin/vacation/components/VacationAdminDetailModal';
import useSort from '@shared/hooks/useSort';

import {
  usePostVacationAllListQuery, // 특정 회사에 등록된 모든 휴가 리스트
  useVacationTypesQuery, // 휴가 타입 목록 조회
  useVacationDetailQuery,
  useGetRemainVacationByEnumberQuery,
  useDeleteAdminVacationMutation,
} from '@features/vacation/hooks';
import useVacationStatus from '@features/vacation/hooks/useVacationStatus';
import useGetTeamQuery from '@features/team/hooks'; // 부서 리스트 조회
import Alert from '@shared/components/atoms/Alert';

import styles from './VacationManagementPage.module.scss';

/**
 * VacationManagementPage
 * - 관리자 휴가 관리 페이지
 * - 휴가 조회 / 선택 삭제 / 상세 조회
 */
export default function VacationManagementPage() {
  /* =========================
   * Alert 상태 관리
   ========================= */
  const [alertState, setAlertState] = useState({
    isOpen: false,
    mode: 'alert', // 'alert' | 'confirm'
    message: '',
    onConfirm: null,
  });
  /* =========================
   * 기본 데이터 조회
   ========================= */

  const { data: vacationTypes = [] } = useVacationTypesQuery(); // 휴가 타입 목록 조회

  const companyCode = useAuthStore((state) => state.companyCode); // 현재 회사 - Zustand store에서 가져오기

  const { data = [] } = useGetTeamQuery(companyCode);
  const teamList = data?.resultData?.teamList ?? []; //부서조회

  /* =========================
   * 검색 / 페이지 상태
   ========================= */
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // 입력용 상태값
  const [draftFilters, setDraftFilters] = useState({
    queryPeriod_start: defaultRange.start,
    queryPeriod_end: defaultRange.end,
    approvalStatus: '', // 전체
    vacationType: '', // 전체
    teamCode: '', // 전체
    name: '', // 전체
  });

  // 실제 API 조회에 사용되는 검색 조건 (조회 버튼 클릭 시 업데이트)
  const [searchParams, setSearchParams] = useState({
    startDate: defaultRange.start.replace(/-/g, ''),
    endDate: defaultRange.end.replace(/-/g, ''),
    stateCode: null,
    vacationType: null,
    teamCode: null,
    name: null,
  });

  //현재페이지
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  /* =========================
   * 선택 / 상세 상태
   ========================= */

  const [selectedRows, setSelectedRows] = useState([]); // 선택된 행 관리

  // 상세 모달 상태
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedVacation, setSelectedVacation] = useState(null);
  const vacationIdx = selectedVacation?.idx;
  const enumber = selectedVacation?.writerInfo?.enumber;

  /* =========================
   * 상세 / 잔여휴가 조회
   ========================= */

  const { data: detailData, isLoading: isDetailLoading } = useVacationDetailQuery(vacationIdx, {
    enabled: !!vacationIdx,
  }); // 상세 조회

  const { data: remainVacationData } = useGetRemainVacationByEnumberQuery(enumber, {
    enabled: !!enumber,
  });

  const vacationStatus = useVacationStatus({
    remainVacation: remainVacationData?.resultData?.remainVacation,
  }); // 잔여 휴가 조회

  /* =========================
   * 관리자 휴가 리스트 조회
   ========================= */
  const {
    data: vacationListResponse,
    refetch: refetchVacationAllList,
    isLoading,
  } = usePostVacationAllListQuery({
    startDate: searchParams.startDate,
    endDate: searchParams.endDate,
    stateCode: searchParams.stateCode || undefined,
    vacationType: searchParams.vacationType || undefined,
    teamCode: searchParams.teamCode || undefined,
    name: searchParams.name || undefined,
  });

  /* =========================
   * 검색 처리
   ========================= */

  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }
  // 테이블 데이터 로직 구현
  const tableData = useMemo(() => {
    return (vacationListResponse?.resultData?.docInfoList ?? []).map((item) => ({
      ...item,
      writerName: item.writerInfo?.name ?? '',
      signerName: item.signerInfo?.name ?? '',
    }));
  }, [vacationListResponse]);

  // 정렬 훅 적용 (기본값: 신청일자(date) 내림차순)
  const { sortedData, sortDirection, sortField, handleSort } = useSort(tableData, {
    field: 'date',
    direction: 'desc',
  });

  //페이지 계산
  const pagedTableData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage]);

  const isAllSelected = useMemo(
    () => tableData.length > 0 && selectedRows.length === tableData.length,
    [selectedRows, tableData],
  );

  // 필터 필드 정의
  const filterFields = useMemo(
    function () {
      // 휴가구분 옵션
      const vacationTypeOptions = [
        { value: '', label: '전체' },
        ...vacationTypes.map(function (type) {
          return {
            value: type.code?.toString() || '',
            label: type.name || '',
          };
        }),
      ];
      const teamListOptions = [
        { value: '', label: '전체' },
        ...teamList.map(function (type) {
          return {
            value: type.teamCode?.toString() || '',
            label: type.team || '',
          };
        }),
      ];

      return [
        // 1열: 조회기간
        {
          key: 'queryPeriod', // UI 식별용
          type: 'dateRange',
          label: '조회기간',
          startKey: 'queryPeriod_start',
          endKey: 'queryPeriod_end',
          startValue: draftFilters.queryPeriod_start || '',
          endValue: draftFilters.queryPeriod_end || '',
        },
        { newRow: true },

        // 2열: 문서상태, 휴가구분
        {
          key: 'approvalStatus',
          type: 'select',
          label: '문서상태',
          value: draftFilters.approvalStatus || '',
          options: [
            { value: '', label: '전체' },
            { value: '2', label: '결재중' },
            { value: '3', label: '결재완료' },
            { value: '4', label: '반려' },
            // { value: '7', label: '관리자취소' },
            { value: '5', label: '삭제' },
          ],
          fill: true,
        },
        {
          key: 'vacationType',
          type: 'select',
          label: '휴가구분',
          value: draftFilters.vacationType || '',
          options: vacationTypeOptions,
          fill: true,
        },
        { newRow: true },

        // 3열: 기안부서, 신청자
        {
          key: 'teamCode',
          type: 'select',
          label: '기안부서',
          value: draftFilters.teamCode || '',
          options: teamListOptions,
          fill: true,
        },
        {
          key: 'name',
          type: 'input',
          label: '기안자',
          value: draftFilters.name || '',
          fill: true,
        },
      ];
    },
    [draftFilters, vacationTypes, teamList],
  );

  /* =========================
   * 상태 텍스트
   ========================= */
  function getStateText(state) {
    const stateMap = {
      0: '임시저장',
      1: '결재 대기',
      2: '결재중',
      3: '결재완료',
      4: '반려',
      7: '삭제', // 결재완료된 휴가의 상태인 관리자취소도 삭제로 표시
      5: '삭제',
    };
    return stateMap[state] || '결재중';
  }
  /* =========================
   * 테이블 컬럼
   ========================= */
  const tableColumns = useMemo(
    () => [
      {
        key: 'checkbox',
        label: '',
        width: '60px',
        sortable: false,
        stopPropagation: true,
        headerRender() {
          return (
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              onClick={(e) => e.stopPropagation()}
              aria-label="전체 선택"
            />
          );
        },
        render(value, item) {
          const isDisabled = item.state === 7 || item.state === 5;

          return (
            <input
              type="checkbox"
              disabled={isDisabled}
              checked={!isDisabled && selectedRows.includes(item.idx)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => !isDisabled && handleRowSelect(item.idx)}
              aria-label="선택"
            />
          );
        },
      },
      {
        key: 'date',
        label: '신청일자',
        width: '160px',
        sortable: true,
      },
      {
        key: 'state',
        label: '문서상태',
        width: '160px',
        sortable: true,
        render: (value) => getStateText(value),
      },
      {
        key: 'writerName',
        label: '기안자',
        width: '160px',
        sortable: true,
        render: (value) => value || '-',
      },
      {
        key: 'signerName',
        label: '결재자',
        width: '160px',
        sortable: true,
        render: (value) => value || '-',
      },
      {
        key: 'vacation_type',
        label: '휴가구분',
        width: '160px',
        sortable: false,
        render: (value, item) => {
          const type = item.vacation_type?.name || '-';
          const cc = item.cc_type?.name;
          return cc ? `${type}-${cc}` : type;
        },
      },
      {
        key: 'start_date',
        label: '시작일자',
        width: '160px',
        sortable: true,
      },
      {
        key: 'end_date',
        label: '종료일자',
        width: '160px',
        sortable: true,
      },
      {
        key: 'period',
        label: '휴가기간',
        width: '160px',
        sortable: true,
      },
      {
        key: 'reason',
        label: '신청내역',
        width: '240px',
        sortable: false,
      },
    ],
    [selectedRows, isAllSelected],
  );

  // 조회 버튼 클릭
  const handleSearch = useCallback(
    function () {
      // 날짜 형식 변환 (YYYY-MM-DD -> YYYYMMDD)
      const startDate = draftFilters.queryPeriod_start?.replace(/-/g, '') || '';
      const endDate = draftFilters.queryPeriod_end?.replace(/-/g, '') || '';

      // stateCode 변환 (빈 문자열이면 null)
      const stateCode =
        draftFilters.approvalStatus !== '' && draftFilters.approvalStatus != null
          ? Number(draftFilters.approvalStatus)
          : null;

      // vacationType 변환 (빈 문자열이면 null)
      const vacationType =
        draftFilters.vacationType === '' ? null : Number(draftFilters.vacationType);

      // teamcode 반환 (빈 문자열이면 null)
      const teamCode = draftFilters.teamCode === '' ? null : Number(draftFilters.teamCode);

      // 기안자 이름 반환(빈 문자열이면 null)
      const name = draftFilters.name?.trim() || null;

      setSearchParams({
        startDate,
        endDate,
        stateCode,
        vacationType,
        teamCode,
        name,
      });
      setCurrentPage(1);
    },
    [draftFilters],
  );

  function handlePageChange(page) {
    setCurrentPage(page);
  }
  /* =========================
   * 선택 처리
   ========================= */
  function handleSelectAll() {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((item) => item.idx));
    }
  }

  function handleRowSelect(id) {
    setSelectedRows(function (prev) {
      if (prev.includes(id)) {
        return prev.filter(function (rowId) {
          return rowId !== id;
        });
      }
      return [...prev, id];
    });
  }

  /* =========================
   * 삭제 처리
   ========================= */
  const { mutateAsync: deleteAdminVacationAsync, isPending } = useDeleteAdminVacationMutation();
  function handleDeleteSelected() {
    if (selectedRows.length === 0) {
      setAlertState({
        isOpen: true,
        mode: 'alert',
        message: '삭제할 항목을 선택해주세요.',
        onConfirm: null,
      });
      return;
    }

    setAlertState({
      isOpen: true,
      mode: 'confirm',
      message: '선택한 휴가를 삭제하시겠습니까?',
      onConfirm: () => handleConfirmDelete(selectedRows), // ✅ 현재 선택값을 직접 전달
    });
  }

  async function handleConfirmDelete(rowsToDelete) {
    try {
      if (!rowsToDelete || rowsToDelete.length === 0) {
        setAlertState({
          isOpen: true,
          mode: 'alert',
          message: '삭제할 항목이 없습니다.',
          onConfirm: null,
        });
        return;
      }

      await Promise.all(rowsToDelete.map((idx) => deleteAdminVacationAsync({ idx })));

      setSelectedRows([]);
      await refetchVacationAllList();
      setIsDetailOpen(false);

      setAlertState({
        isOpen: true,
        mode: 'alert',
        message: '삭제되었습니다.',
        onConfirm: null,
      });
    } catch (err) {
      console.error(err);

      setAlertState({
        isOpen: true,
        mode: 'alert',
        message: '일부 휴가 삭제 중 오류가 발생했습니다.',
        onConfirm: null,
      });
    }
  }

  function handleDeleteSingle(idx) {
    setAlertState({
      isOpen: true,
      mode: 'confirm',
      message: '해당 휴가를 삭제하시겠습니까?',
      onConfirm: () => handleConfirmDelete([idx]), // ✅ state 안 거치고 바로 삭제
    });
  }

  function handleRowClick(item) {
    setSelectedVacation({
      idx: item.idx,
      writerInfo: item.writerInfo,
    });
    setIsDetailOpen(true);
  }

  //초기화
  function handleReset() {
    // 1. UI 입력값 초기화
    setDraftFilters({
      queryPeriod_start: defaultRange.start,
      queryPeriod_end: defaultRange.end,
      approvalStatus: '',
      vacationType: '',
      teamCode: '',
      name: '',
    });

    // 2. 실제 조회 조건 초기화 (→ React Query 자동 재조회)
    setSearchParams({
      startDate: defaultRange.start.replace(/-/g, ''),
      endDate: defaultRange.end.replace(/-/g, ''),
      stateCode: null,
      vacationType: null,
      teamCode: null,
      name: null,
    });

    // 3. 페이지도 처음으로
    setCurrentPage(1);

    // (선택) 체크박스 선택도 초기화
    setSelectedRows([]);
  }

  function handleDeleteSingle(idx) {
    setAlertState({
      isOpen: true,
      mode: 'confirm',
      message: '해당 휴가를 삭제하시겠습니까?',
      onConfirm: () => handleConfirmDelete([idx]), // ✅ state 안 거치고 바로 삭제
    });
  }

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '휴가관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              {/* 검색/필터 섹션 */}
              <div className={styles.filterWrapper}>
                <SearchFilter
                  fields={filterFields}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  showExcelButton={false}
                  extraContentPosition="actions"
                  extraContent={
                    <div className={styles.queryNotice}>
                      <ul>
                        <li>휴가 조회 시 전자결재를 통한 휴가등록이력만 조회 가능합니다.</li>
                      </ul>
                    </div>
                  }
                  onReset={handleReset}
                  documnetCount={tableData.length}
                />
              </div>
              {/* 선택삭제 버튼 */}
              <div className={styles.buttonWrap}>
                <Button
                  type="button"
                  className={styles.deleteButton}
                  onClick={handleDeleteSelected}
                  disabled={selectedRows.length === 0 || isPending}
                >
                  선택 삭제
                </Button>
              </div>
              {/* 데이터 테이블 */}
              <div className={styles.tableWrapper}>
                {isLoading ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.circularProgress} />
                    <div className={styles.loadingMessage}>
                      휴가 신청 목록을 불러오는 중입니다...
                    </div>
                  </div>
                ) : (
                  //  : tableData.length === 0 ? (
                  //  <div className={styles.emptyCell}>휴가 신청 내역이 없습니다.</div>
                  // )
                  <ApprovalDataTable
                    columns={tableColumns}
                    data={pagedTableData}
                    onRowClick={handleRowClick}
                    getRowKey={(item) => item.idx}
                    emptyContent={
                      <div className={styles.emptyCell}>휴가 신청 내역이 없습니다.</div>
                    }
                    onSort={handleSort}
                    sortField={{ field: sortField, direction: sortDirection }}
                  />
                )}
              </div>
              {/* 페이지네이션 */}
              <div className={styles.paginationWrapper}>
                {tableData.length > 0 && (
                  <Pagination
                    pageSize={pageSize}
                    totalItems={sortedData.length}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            </ContentCard>
          ),
        }}
      />
      <VacationAdminDetailModal
        isOpen={isDetailOpen && !!selectedVacation}
        vacation={selectedVacation}
        vacationData={detailData}
        vacationStatus={vacationStatus}
        allList={tableData}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedVacation(null);
        }}
        isLoading={isDetailLoading}
        onDelete={handleDeleteSingle}
      />
      <Alert
        isOpen={alertState.isOpen}
        mode={alertState.mode}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        onClose={() =>
          setAlertState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
      />
    </AdminLayout>
  );
}
